import { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, differenceInCalendarDays } from "date-fns";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import type { Deal } from "../../lib/types";
import DealLink from "../DealLink";
import DealCallText from "../DealCallText";
import { daysInStage } from "../../lib/dealAge";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

/**
 * Chad's SLA Issues Tab — Deals past their scheduling SLA.
 * PRE SLA: 3 days from RS assignment.
 * Build SLA: homeowner preferred date or 7 days from stage entry.
 * Adjuster SLA: 5 days from PRE completion.
 */

type Urgency = "overdue" | "warning" | "ok";
type IssueType = "pre_inspection" | "adjuster_meeting" | "build";

type SLAIssue = {
  id: string;
  deal: Deal;
  type: IssueType;
  urgency: Urgency;
  label: string;
  daysOverdue: number;
  rmName: string;
};

const TYPE_LABELS: Record<IssueType, string> = {
  pre_inspection: "Pre-Inspection",
  adjuster_meeting: "Adjuster Meeting",
  build: "Build",
};
const TYPE_CHIP: Record<IssueType, string> = {
  pre_inspection: "bg-amber-100 text-amber-700",
  adjuster_meeting: "bg-indigo-100 text-indigo-700",
  build: "bg-emerald-100 text-emerald-700",
};
// Urgency border colors handled inline in the JSX below

const COLS =
  "id,homeowner_name,homeowner_phone,property_street,property_city,property_state,property_zip,claim_number,stage,phase,days_in_stage,stage_entered_at,phase_entered_at,created_at,owner_id,split_rm_id,pre_inspection_date,pre_inspection_assigned_to,pre_inspection_completed_date,rs_assignment_date,ho_preferred_build_date,build_scheduled_date,adjuster_meeting_date,insurance_company";

function formatAddr(d: Deal) {
  return [d.property_street, [d.property_city, d.property_state].filter(Boolean).join(", "), d.property_zip].filter(Boolean).join(" ");
}

export default function ChadSLATab() {
  const [issues, setIssues] = useState<SLAIssue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState<"all" | IssueType>("all");

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("deals", reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      const today = new Date();

      // Fetch PRE SLA violations: claims phase, no pre_inspection_date, RS assigned > 3 days ago
      const [{ data: preDeals, error: e1 }, { data: adjDeals, error: e2 }, { data: buildDeals, error: e3 }] =
        await Promise.all([
          supabase.from("deals").select(COLS).eq("phase", "claims").is("pre_inspection_date", null).not("stage", "in", "(closed_won,closed_lost,cancelled)"),
          supabase.from("deals").select(COLS).eq("phase", "claims").is("adjuster_meeting_date", null).not("pre_inspection_date", "is", null).not("stage", "in", "(closed_won,closed_lost,cancelled)"),
          supabase.from("deals").select(COLS).eq("stage", "schedule_build").is("build_scheduled_date", null),
        ]);

      const err = e1 ?? e2 ?? e3;
      if (err) { if (!cancelled) setError(err.message); return; }

      // Resolve RM names
      const ownerIds = new Set<string>();
      [...(preDeals ?? []), ...(adjDeals ?? []), ...(buildDeals ?? [])].forEach((d) => {
        if (d.owner_id) ownerIds.add(d.owner_id);
      });
      const memberMap: Record<string, string> = {};
      if (ownerIds.size > 0) {
        const { data: ms } = await supabase.from("team_roster").select("id,full_name").in("id", [...ownerIds]);
        (ms ?? []).forEach((m) => { memberMap[m.id] = m.full_name; });
      }

      const results: SLAIssue[] = [];

      // PRE SLA: 3 days from RS assignment
      (preDeals ?? []).forEach((d) => {
        const assigned = d.rs_assignment_date ? new Date(d.rs_assignment_date) : null;
        if (!assigned) return;
        const slaTarget = addDays(assigned, 3);
        const daysOver = differenceInCalendarDays(today, slaTarget);
        if (daysOver < 0) return; // not overdue yet
        results.push({
          id: `pre-${d.id}`,
          deal: d as Deal,
          type: "pre_inspection",
          urgency: daysOver > 0 ? "overdue" : "warning",
          label: daysOver > 0
            ? `PRE overdue by ${daysOver} day${daysOver === 1 ? "" : "s"}`
            : "PRE due today",
          daysOverdue: daysOver,
          rmName: d.owner_id ? memberMap[d.owner_id] ?? "—" : "—",
        });
      });

      // Adjuster SLA: 5 days from PRE completion
      (adjDeals ?? []).forEach((d) => {
        // Skip deals that already have a meeting on the calendar or are past claims.
        if (d.adjuster_meeting_date != null || d.stage === "waiting_on_claims") return;
        const preDate = d.pre_inspection_completed_date ?? d.pre_inspection_date;
        if (!preDate) return;
        const slaTarget = addDays(new Date(preDate), 5);
        const daysOver = differenceInCalendarDays(today, slaTarget);
        if (daysOver < 0) return;
        results.push({
          id: `adj-${d.id}`,
          deal: d as Deal,
          type: "adjuster_meeting",
          urgency: daysOver > 0 ? "overdue" : "warning",
          label: daysOver > 0
            ? `Adjuster overdue by ${daysOver} day${daysOver === 1 ? "" : "s"}`
            : "Adjuster due today",
          daysOverdue: daysOver,
          rmName: d.owner_id ? memberMap[d.owner_id] ?? "—" : "—",
        });
      });

      // Build SLA: preferred date or 7 days from stage entry
      (buildDeals ?? []).forEach((d) => {
        const target = d.ho_preferred_build_date
          ? new Date(d.ho_preferred_build_date)
          : addDays(new Date(), -(daysInStage(d) - 7));
        const daysOver = differenceInCalendarDays(today, target);
        if (daysOver < 0) return;
        results.push({
          id: `build-${d.id}`,
          deal: d as Deal,
          type: "build",
          urgency: daysOver > 0 ? "overdue" : "warning",
          label: d.ho_preferred_build_date
            ? `Past preferred date by ${daysOver} day${daysOver === 1 ? "" : "s"}`
            : `Build scheduling overdue (${daysInStage(d)} days in stage)`,
          daysOverdue: daysOver,
          rmName: d.owner_id ? memberMap[d.owner_id] ?? "—" : "—",
        });
      });

      results.sort((a, b) => b.daysOverdue - a.daysOverdue);
      if (!cancelled) setIssues(results);
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const filtered = useMemo(() => {
    if (!issues) return null;
    if (filter === "all") return issues;
    return issues.filter((i) => i.type === filter);
  }, [issues, filter]);

  if (error)
    return <div className="bg-red-50 text-red-700 p-4 rounded">Couldn't load SLA issues — {error}</div>;
  if (!filtered)
    return <div className="text-slate-500 p-4">Loading SLA issues…</div>;

  const counts = {
    all: issues?.length ?? 0,
    pre_inspection: issues?.filter((i) => i.type === "pre_inspection").length ?? 0,
    adjuster_meeting: issues?.filter((i) => i.type === "adjuster_meeting").length ?? 0,
    build: issues?.filter((i) => i.type === "build").length ?? 0,
  };

  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">
        Deals past their scheduling SLA. PRE = 3 days from RS assignment, Adjuster = 5 days from PRE, Build = preferred date or 7 days.
      </p>

      {/* Filter pills */}
      <div className="flex gap-1 flex-wrap mb-4">
        {([
          { key: "all" as const, label: `All (${counts.all})` },
          { key: "pre_inspection" as const, label: `PRE (${counts.pre_inspection})` },
          { key: "adjuster_meeting" as const, label: `Adjuster (${counts.adjuster_meeting})` },
          { key: "build" as const, label: `Build (${counts.build})` },
        ]).map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={
              "px-2.5 py-1 rounded-xl text-[10px] font-semibold border transition " +
              (filter === f.key
                ? "bg-teal-500 text-white border-teal-500"
                : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-slate-500 text-[12px]">
          No SLA violations
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((issue) => {
            const slaDotColor = issue.urgency === "overdue" ? "bg-red-600" : issue.urgency === "warning" ? "bg-amber-500" : "bg-emerald-500";
            return (
              <div
                key={issue.id}
                className={
                  "rounded-lg px-3 py-2.5 border-l-[3px] text-[11px] transition hover:shadow-md " +
                  (issue.urgency === "overdue"
                    ? "border-l-red-600 bg-white"
                    : issue.urgency === "warning"
                    ? "border-l-amber-500 bg-white"
                    : "border-l-emerald-500 bg-white")
                }
              >
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${slaDotColor}`} />
                  <span className="font-bold text-slate-900 text-[12px]">
                    <DealLink id={issue.deal.id}>{issue.deal.homeowner_name ?? "(no name)"}</DealLink>
                  </span>
                  <span className={"text-[10px] font-bold px-1.5 py-0.5 rounded " + TYPE_CHIP[issue.type]}>
                    {TYPE_LABELS[issue.type]}
                  </span>
                </div>
                <div className="text-slate-500 text-[10px] mt-0.5">
                  {isAddressLinkable(issue.deal) ? (
                    <a href={mapsHref(issue.deal)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline hover:text-slate-700">{formatAddr(issue.deal)}</a>
                  ) : (
                    formatAddr(issue.deal)
                  )} · RM: {issue.rmName}
                  {issue.deal.claim_number && <> · #{issue.deal.claim_number}</>}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <div className="text-red-600 font-semibold text-[10px]">
                    {issue.label}
                  </div>
                  <DealCallText dealId={issue.deal.id} homeownerName={issue.deal.homeowner_name} homeownerPhone={issue.deal.homeowner_phone} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
